<template>
  <div class="max-w-115rem">
    <div class="row">
      <div class="col-sm-6">
        <h6>Identity</h6>

        <ValidationProvider
          v-if="provisionedAccount && !userSCIMManaged"
          v-slot="{ errors }"
          :rules="`${nameRequired}|min_name:2|max_name:25`"
          name="first_name"
        >
          <div
            id="first-name"
            class="form-group mb-16"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label">First name</label>
            <AppInput
              v-model="firstName"
              maxlength="255"
            />

            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          v-if="provisionedAccount && !userSCIMManaged"
          v-slot="{ errors }"
          :rules="`${nameRequired}|min_name:2|max_name:25`"
          name="last_name"
        >
          <div
            id="last-name"
            class="form-group mb-16"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label">Last name</label>
            <AppInput
              v-model="lastName"
              maxlength="255"
            />

            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          v-if="provisionedAccount"
          v-slot="{ errors }"
          name="time_zone"
        >
          <div
            id="time-zone"
            class="form-group mb-16"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label">Time zone</label>
            <AppSelect
              v-model="timeZoneId"
              :options="timeZoneOptions"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          v-if="hasCorporateApp"
          v-slot="{ errors }"
          name="os_username"
        >
          <div
            id="os-username"
            class="form-group mb-16"
            :class="{ 'has-error': errors[0] }"
          >
            <div class="label-with-tooltip">
              <label class="control-label">OS Username</label>
              <i
                v-if="userSCIMManaged"
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                data-original-title="Member managed by SCIM"
              />
            </div>
            <AppInput
              v-model="osUsername"
              :readonly="disabled || userSCIMManaged"
              placeholder="No OS username"
              maxlength="255"
            />

            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
        <ValidationProvider name="employee_id">
          <div
            id="employee-id"
            class="form-group"
          >
            <div class="label-with-tooltip">
              <label class="control-label">Employee ID</label>
              <i
                v-if="userSCIMManaged"
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                data-original-title="Member managed by SCIM"
              />
            </div>
            <AppInput
              v-model="employeeId"
              :readonly="disabled || userSCIMManaged"
              placeholder="No employee ID"
              maxlength="255"
            />
          </div>
        </ValidationProvider>
        <div class="form-group">
          <div class="birthday d-flex justify-content-between align-items-center mb-5">
            <label class="control-label mb-0">Birthday</label>
            <div
              v-if="birthdayYear"
              class="member-age text-lighter"
            >
              {{ memberAge }}
            </div>
          </div>
          <div class="month-day-select d-flex flex-column flex-xl-row gap-10">
            <AppSelect
              v-model="birthdayMonth"
              class="month-day-select-month"
              :options="birthdayMonthOptions"
              :select2-options="select2OptionsMonth"
              @input="onBirthdayMonthChange"
            />
            <ValidationProvider
              ref="birthdayYear"
              v-slot="{ errors }"
              name="birthdayDay"
              :rules="birthdayRequired"
              :model-value="birthdayDay"
            >
              <div
                class="form-group month-day-select-day"
                :class="{ 'has-error': errors[0] }"
              >
                <AppSelect
                  v-model="birthdayDay"
                  :options="birthdayDayOptions"
                  :select2-options="select2OptionsDay"
                  @input="onBirthdayDayChange"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <ValidationProvider
              ref="birthdayYear"
              v-slot="{ errors }"
              name="birthdayYear"
              :rules="`${birthdayRequired}|valid_leap_year:${birthdayMonth},${birthdayDay}`"
              :model-value="birthdayYear"
            >
              <div
                class="form-group month-day-select-year"
                :class="{ 'has-error': errors[0] }"
              >
                <AppSelect
                  v-model="birthdayYear"
                  :options="birthdayYearOptions"
                  :select2-options="select2OptionsYear"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
      </div>
      <div class="col-sm-6">
        <MemberIpLocationCard
          v-if="membership.ip_location_data"
          :ip-location="membership.ip_location_data"
          :membership-id="membership.id"
          :organization-id="organizationId"
          :user-name="user.name"
          :audit-log-url="membership.audit_log_url"
        />
        <template v-else>
          <div
            id="ip-address"
            class="form-group"
          >
            <div class="label-with-tooltip">
              <label class="control-label">IP Address</label>
              <i
                v-tooltip
                class="hi hi-info-filled a-tooltip hi-primary"
                data-placement="right"
                data-original-title="Free during Hubstaff People BETA"
              />
            </div>
            <AppInput
              v-model="user.last_sign_in_ip"
              readonly
              placeholder="No IP address"
            />
            <span class="help-block"></span>
          </div>

          <div
            id="sign-in-date"
            class="form-group"
          >
            <div class="label-with-tooltip">
              <label class="control-label">Date</label>
              <i
                v-tooltip
                class="hi hi-info-filled a-tooltip hi-primary"
                data-placement="right"
                data-original-title="Free during Hubstaff People BETA"
              />
            </div>
            <AppInput
              :value="lastSignIn"
              readonly
              placeholder="No sign-in date"
            />
            <span class="help-block"></span>
          </div>

          <div class="people-addon-preview primary-border small">
            <img
              :src="hubstaffBetaImage"
              alt="Hubstaff People Beta"
            />
            <div>Try these new features for free while Hubstaff People is in BETA</div>
            <ul>
              <li>View IP addresses</li>
              <li>Create and manage custom fields</li>
            </ul>
          </div>
        </template>
      </div>
    </div>
    <h6>Contact</h6>
    <div class="row">
      <div class="col-sm-6">
        <div
          id="work-address-read-only"
          class="form-group"
        >
          <div class="label-with-extra">
            <div class="label-with-tooltip">
              <label class="control-label">Work address</label>
              <i
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                data-original-title="Where work physically happens"
              />
            </div>
            <a
              class="strong clickable"
              @click="onTabSelected"
            >
              Edit work address
            </a>
          </div>
          <AppInput
            :value="workAddress"
            placeholder="No address"
            readonly
          />
        </div>

        <ValidationProvider
          v-slot="{ errors }"
          name="work-email"
          rules="required"
        >
          <div
            id="work-email"
            class="form-group"
            :class="{ 'has-error': errors[0] }"
          >
            <div class="label-with-tooltip">
              <label class="control-label">Work email</label>
              <i
                v-if="!provisionedAccount || userSCIMManaged"
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                :data-original-title="!provisionedAccount ? 'This Hubstaff Account data can only be changed by the member' : 'Member managed by SCIM'"
              />
            </div>
            <AppInput
              v-model="email"
              :readonly="!provisionedAccount || userSCIMManaged"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors }"
          name="work-phone"
          :model-value="workPhone"
        >
          <div
            id="work-phone"
            class="form-group mb-10"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label">Work phone</label>
            <vue-tel-input
              v-model="workPhone"
              maxlength="30"
              default-country="us"
              dynamic-placeholder="dynamic-placeholder"
              mode="international"
              wrapper-classes="form-control input-default"
              valid-characters-only="valid-characters-only"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="col-sm-6">
        <ValidationProvider
          ref="homeAddress"
          v-slot="{ errors }"
          name="homeAddress"
        >
          <div
            id="home-address"
            class="form-group"
            :class="{ 'has-error': errors[0] }"
          >
            <div class="label-with-extra">
              <div class="label-with-tooltip">
                <label class="control-label">Home address</label>
                <i
                  v-tooltip
                  class="hi hi-info-filled a-tooltip"
                  data-placement="right"
                  data-original-title="Where member lives/pays taxes"
                />
              </div>
              <div class="text-lighter">
                <AppCheckbox
                  v-model="homeMailingAddress"
                  :disabled="!homeAddress"
                >
                  Mailing address
                </AppCheckbox>
              </div>
            </div>
            <GmapAutocomplete
              id="home-address-autocomplete"
              class="form-control"
              :value="homeAddress"
              placeholder="Search for an address"
              @place_changed="onSetAddress('homeAddress', $event)"
              @input="onChangeAddress('homeAddress', $event)"
              @blur="onManualAddressEntry('homeAddress', $event)"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <ValidationProvider name="personal_email">
          <div
            id="personal-email"
            class="form-group"
          >
            <label class="control-label">Personal email</label>
            <AppInput
              v-model="personalEmail"
              placeholder="name@example.com"
              maxlength="255"
            />
            <span class="help-block"></span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors }"
          name="personal-phone"
          :model-value="personalPhone"
        >
          <div
            id="personal-phone"
            class="form-group mb-10"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label">Personal phone</label>
            <vue-tel-input
              v-model="personalPhone"
              maxlength="30"
              default-country="us"
              dynamic-placeholder="dynamic-placeholder"
              mode="international"
              wrapper-classes="form-control input-default"
              valid-characters-only="valid-characters-only"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>
    <div class="d-flex justify-content-between my-20">
      <div class="d-flex align-items-center gap-5">
        <h6 class="m-0">Custom fields</h6>
        <i
          v-tooltip
          class="hi hi-info-filled a-tooltip hi-primary"
          data-placement="right"
          data-original-title="Free during Hubstaff People BETA"
        />
      </div>
      <a
        :href="memberProfilesLink"
        class="strong manage-custom-fields"
      >
        Manage custom fields
      </a>
    </div>

    <div class="row">
      <div class="col-sm-6">
        <div
          v-for="field in leftCustomFieldsList"
          :key="field.key"
          class="form-group custom-fields-left"
        >
          <label class="control-label">{{ field.title }}</label>
          <ValidationProvider name="field.title">
            <AppInput
              :value="customFieldValue(field)"
              @input="updateCustomFieldValue(field, $event)"
            />
          </ValidationProvider>
        </div>
      </div>
      <div class="col-sm-6">
        <div
          v-for="field in rightCustomFieldsList"
          :key="field.key"
          class="form-group custom-fields-right"
        >
          <label class="control-label">{{ field.title }}</label>
          <ValidationProvider name="field.title">
            <AppInput
              :value="customFieldValue(field)"
              @input="updateCustomFieldValue(field, $event)"
            />
          </ValidationProvider>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';

import dayjs from 'dayjs';
import dayjsLocaleData from 'dayjs/plugin/localeData';
import { OrganizationsResource } from '~/resources';
import { HUBSTAFF_PEOPLE_BETA_TRANSPARENT } from '~/rails_assets';
import { formatDate } from '~/utils/time_utils';
import { extend } from '~/utils/vee_validate_compat';
import { min, max } from '@vee-validate/rules';
import AddressMixin from '~/mixins/address_utils';
import BirthdayMixin from '~/mixins/birthday_utils';
import MemberIpLocationCard from './MemberIpLocationCard.vue';
dayjs.extend(dayjsLocaleData);

extend('min_name', {
  validate: min,
  message: 'is too short (minimum is two characters)',
});

extend('max_name', {
  validate: max,
  message: 'is too long (maximum is twenty-five characters)',
});

export default {
  name: 'MemberDialogTabAbout',
  components: { MemberIpLocationCard },
  mixins: [AddressMixin, BirthdayMixin],
  inject: ['selectTab'],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    hubstaffBetaImage: HUBSTAFF_PEOPLE_BETA_TRANSPARENT,
  }),
  computed: {
    ...mapState(['membership', 'organization', 'errors', 'changes']),
    ...mapState({
      user: (state) => state.membership.user,
      timeZoneOptions: (state) => state.time_zones,
    }),
    ...mapGetters(['hasFeature', 'organizationId']),
    ...mapFormFields('membership', {
      employeeId: 'employee_id',
      osUsername: 'os_username',
      startedOnRaw: 'started_on',
      birthdayMonth: 'birthday_month',
      birthdayDay: 'birthday_day',
      birthdayYear: 'birthday_year',
      workAddress: 'work_address',
      workMailingAddress: 'work_mailing_address',
      homeAddress: 'home_address',
      homeAddressState: 'home_address_state',
      homeAddressCountry: 'home_address_country',
      homeMailingAddress: 'home_mailing_address',
      personalEmail: 'personal_email',
      workPhone: 'work_phone',
      personalPhone: 'personal_phone',
      customFields: 'custom_fields',
      firstName: 'user.first_name',
      lastName: 'user.last_name',
      fullName: 'user.name',
      email: 'user.email',
      timeZoneId: 'user.time_zone_id',
    }),
    startedOn: {
      get() {
        return this.startedOnRaw ? moment(this.startedOnRaw) : null;
      },
      set(newValue) {
        this.startedOnRaw = newValue.format('YYYY-MM-DD');
      },
    },
    select2OptionsMonth() {
      return {
        placeholder: 'Select a month',
        allowClear: true,
        width: '100%',
      };
    },
    select2OptionsDay() {
      return {
        placeholder: 'Select a day',
        allowClear: true,
        width: '100%',
      };
    },
    select2OptionsYear() {
      return {
        placeholder: 'Select a year',
        allowClear: true,
        width: '100%',
      };
    },
    customFieldsList() {
      return this.organization.member_profile_fields;
    },
    hasCorporateApp() {
      return this.hasFeature('corporate');
    },
    provisionedAccount() {
      return this.hasCorporateApp && this.membership.from_account_provisioning;
    },
    customFieldsMiddleIndex() {
      return Math.ceil(this.customFieldsList.length / 2);
    },
    leftCustomFieldsList() {
      return this.customFieldsList.slice(0, this.customFieldsMiddleIndex);
    },
    rightCustomFieldsList() {
      return this.customFieldsList.slice(this.customFieldsMiddleIndex);
    },
    memberProfilesLink() {
      return OrganizationsResource.profilesSettingsLink({ id: this.organizationId });
    },
    lastSignIn() {
      return formatDate(dayjs(this.user.last_sign_in_at));
    },
    birthdayRequired() {
      return this.birthdayMonth ? 'required' : '';
    },
    nameRequired() {
      return this.changes.user?.first_name || this.changes.user?.last_name ? 'required' : '';
    },
    userSCIMManaged() {
      return this.user.scim_managed;
    },
  },
  watch: {
    homeMailingAddress(newVal) {
      if (newVal) {
        this.workMailingAddress = false;
      }
    },
    homeAddress(newVal) {
      if (!newVal) {
        this.homeMailingAddress = false;
      }
    },
  },
  methods: {
    ...mapActions(['updateCustomFields']),
    updateCustomFieldValue(field, value) {
      this.updateCustomFields({ [field.key]: value });
    },
    onBirthdayMonthChange(newValue) {
      if (newValue === undefined) {
        this.birthdayMonth = '';
        this.birthdayDay = '';
        this.birthdayYear = '';
      }
    },
    onBirthdayDayChange(newValue) {
      if (newValue === undefined) {
        this.birthdayDay = '';
        this.birthdayYear = '';
      }
    },
    customFieldValue(field) {
      return this.customFields && this.customFields[field.key];
    },
    onTabSelected() {
      this.selectTab(null, 'employment', 'work-address');
    },
  },
};
</script>
